import React, { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { CheckCircle2, Download, ImageUp, LayoutTemplate, Loader2, Plus, Save, Sparkles, Trash2, X } from "lucide-react";
import { ACTIVE_CUSTOMER_ID_CHANGED_EVENT } from "../../hooks/useMerchantStores";
import {
  ASPECT_RATIOS,
  REFERENCE_MAX_BYTES,
  REFERENCE_TYPES,
  approveTemplate,
  createTemplate,
  deletePrompt,
  deleteTemplate,
  ensureInitialTemplate,
  generateTemplateBackground,
  listBackgroundPrompts,
  listTemplates,
  saveBackgroundPrompt,
  updateTemplate,
  uploadTemplateAsset,
} from "./imageCreationService";
import { downloadTemplatePreview } from "./templateExport";

const inputCls =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none disabled:bg-gray-50";
const labelCls = "text-sm font-medium text-gray-900";

const COLOR_FIELDS = [
  // Keys stay primary/secondary/accent (saved templates + AI prompt use them); only the labels say what each colour does.
  // "secondary" has no picker: nothing on the template or export uses it (it is still saved and sent to the AI prompt).
  ["primary", "Heading"],
  ["accent", "Border line"],
  ["background", "Background"],
  ["text", "Text"],
];
// Font choices for the Heading / Body dropdowns. `wght` is what Google Fonts offers for that family
// (asking for a weight a family doesn't have makes the whole stylesheet request fail).
const FONT_OPTIONS = [
  { name: "Montserrat", wght: "400;600;700" },
  { name: "Poppins", wght: "400;600;700" },
  { name: "Inter", wght: "400;600;700" },
  { name: "Roboto", wght: "400;500;700" },
  { name: "Open Sans", wght: "400;600;700" },
  { name: "Lato", wght: "400;700" },
  { name: "Nunito", wght: "400;600;700" },
  { name: "Raleway", wght: "400;600;700" },
  { name: "Oswald", wght: "400;600;700" },
  { name: "Playfair Display", wght: "400;600;700" },
  { name: "Merriweather", wght: "400;700" },
  { name: "Lora", wght: "400;600;700" },
  { name: "Abril Fatface", wght: "400" },
  { name: "Bebas Neue", wght: "400" },
  { name: "Lobster", wght: "400" },
  { name: "Pacifico", wght: "400" },
  { name: "Dancing Script", wght: "400;600;700" },
];
const FONTS_LINK_ID = "bt-google-fonts";
// Loads the dropdown fonts once so the preview (and the exported image) can really use them.
function ensureFontsLoaded() {
  if (typeof document === "undefined" || document.getElementById(FONTS_LINK_ID)) return;
  const link = document.createElement("link");
  link.id = FONTS_LINK_ID;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?${FONT_OPTIONS.map((f) => `family=${f.name.replace(/ /g, "+")}:wght@${f.wght}`).join("&")}&display=swap`;
  document.head.appendChild(link);
}
const NAME_POSITIONS = [
  ["top", "Top"],
  ["center", "Center"],
];
// Starting points for the background description. They only fill the box; nothing is created until the merchant clicks Create.
export const PROMPT_SUGGESTIONS = [
  { label: "Watercolour leaves", prompt: "Soft watercolour leaves and flowers along the edges on a warm cream paper texture" },
  { label: "Rustic wood", prompt: "Dark rustic wood planks with warm, moody lighting and a subtle vignette" },
  { label: "Modern geometric", prompt: "Clean modern geometric pattern in soft pastel shades with plenty of open space" },
  { label: "Fresh herbs", prompt: "Hand-drawn fresh herbs and vegetables in a light, airy repeating pattern on a bright background" },
];
const HEX = /^#[0-9a-fA-F]{6}$/;
const PHONE_RE = /^[0-9+()\-.\s]{5,30}$/;
const WEBSITE_RE = /^(https?:\/\/)?([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(:\d{2,5})?(\/\S*)?$/i;

/** Same rules as the server (baseTemplateService.js), so mistakes show up before saving. The server still decides. */
export const storeInfoErrors = (brand) => {
  const errors = {};
  const c = brand.contact || {};
  const phone = String(c.phone || "").trim();
  if (phone && !(PHONE_RE.test(phone) && phone.replace(/\D/g, "").length >= 5)) errors.phone = "Enter a valid phone number, for example +44 20 7946 0000.";
  const website = String(c.website || "").trim();
  if (website && !WEBSITE_RE.test(website)) errors.website = "Enter a valid website, for example www.example.com.";
  return errors;
};

const BACKGROUNDS = [
  ["solid", "Solid colour"],
  ["image", "Keep the reference image's background"],
  ["ai", "AI-designed background (describe it)"],
];

// A blank form. Nothing here is specific to any business.
const DEFAULT_COLORS = { primary: "#1f2937", secondary: "#f3f4f6", accent: "#f59e0b", background: "#ffffff", text: "#111827" };
const blankDraft = (prefill) => ({
  id: null,
  name: "",
  status: "draft",
  source: "manual",
  config: {
    brand: {
      businessName: prefill?.brand?.businessName || "",
      contact: { phone: prefill?.brand?.contact?.phone || "", website: prefill?.brand?.contact?.website || "", address: prefill?.brand?.contact?.address || "" },
      colors: { ...DEFAULT_COLORS },
      fonts: { heading: "", body: "" },
    },
    background: { type: "solid", color: "#ffffff", note: "", prompt: "" },
    layout: { aspectRatio: "16:9", preset: "grid", storeNamePosition: "top", storeNameContrast: 40, logoPosition: "top", logoContrast: 100, logoSize: 100, slots: { image: { enabled: true }, name: { enabled: true }, price: { enabled: true }, description: { enabled: true }, promo: { enabled: false, text: "" } } },
  },
  referenceImageKey: null,
  referenceImageUrl: null,
  logoKey: null,
  logoUrl: null,
  backgroundImageKey: null,
  backgroundImageUrl: null,
});

// Fills anything an older template doesn't have (store contact details, name placement) with defaults.
const withDefaults = (config) => {
  const d = blankDraft().config;
  const c = config || {};
  return {
    ...d,
    ...c,
    brand: { ...d.brand, ...c.brand, colors: { ...d.brand.colors, ...c.brand?.colors }, fonts: { ...d.brand.fonts, ...c.brand?.fonts }, contact: { ...d.brand.contact, ...c.brand?.contact } },
    background: { ...d.background, ...c.background },
    layout: {
      ...d.layout,
      ...c.layout,
      storeNamePosition: c.layout?.storeNamePosition === "center" ? "center" : "top",
      logoPosition: c.layout?.logoPosition === "center" ? "center" : "top",
      logoSize: Number.isInteger(c.layout?.logoSize) ? Math.min(400, Math.max(50, c.layout.logoSize)) : 100,
      logoContrast: Number.isInteger(c.layout?.logoContrast) ? Math.min(100, Math.max(10, c.layout.logoContrast)) : 100,
      storeNameContrast: Number.isInteger(c.layout?.storeNameContrast) ? Math.min(100, Math.max(10, c.layout.storeNameContrast)) : 40,
      slots: { ...d.layout.slots, ...c.layout?.slots },
    },
  };
};
const fromTemplate = (t) => ({ ...blankDraft(), ...t, config: withDefaults(t.config) });
const clone = (v) => JSON.parse(JSON.stringify(v));
const fieldsOf = (d) => ({ name: d.name, config: d.config, referenceImageKey: d.referenceImageKey, logoKey: d.logoKey, backgroundImageKey: d.backgroundImageKey });

/** Upload box for the template's reference image or logo: uploads right away and keeps only the returned key. */
function AssetPicker({ label, hint, url, onUploaded, onClear, disabled, kind }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pick = async (file) => {
    if (!file) return;
    setError("");
    if (!REFERENCE_TYPES.includes(file.type)) return setError("Please upload a PNG, JPG or WebP image.");
    if (file.size > 8 * 1024 * 1024 || file.size > REFERENCE_MAX_BYTES) return setError("Image must be 8 MB or smaller.");
    setBusy(true);
    try {
      onUploaded(await uploadTemplateAsset(file, kind));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-1.5">
      <span className={labelCls}>{label}</span>
      <span className="text-xs text-gray-500">{hint}</span>
      {url ? (
        <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-2.5">
          <img src={url} alt={label} className="h-14 w-24 flex-none rounded-lg bg-gray-200 object-contain" />
          <button type="button" onClick={onClear} disabled={disabled || busy} className="ml-auto inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:border-gray-400 disabled:opacity-50">
            <X size={12} /> Remove
          </button>
        </div>
      ) : (
        <label className={`inline-flex w-fit cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:border-indigo-400 hover:text-indigo-700 ${disabled || busy ? "pointer-events-none opacity-50" : ""}`}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <ImageUp size={15} />}
          {busy ? "Uploading…" : "Choose image"}
          <input type="file" accept={REFERENCE_TYPES.join(",")} className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
      )}
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}

/** One colour: a picker plus a hex box for typing an exact value. Only valid hex values reach the template. */
function FontSelect({ id, value, onChange, disabled }) {
  useEffect(() => { ensureFontsLoaded(); }, []);
  const known = FONT_OPTIONS.some((f) => f.name === value);
  return (
    <select id={id} className={inputCls} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
      <option value="">Default (clean sans-serif)</option>
      {/* a font typed in before this list existed stays selectable */}
      {value && !known && <option value={value}>{value}</option>}
      {FONT_OPTIONS.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
    </select>
  );
}

function ColorField({ label, value, onChange, disabled }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const valid = HEX.test(text);
  return (
    <div className="flex items-center gap-2 text-xs text-gray-700">
      <input type="color" aria-label={`${label} colour`} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value.toLowerCase())} className="h-8 w-10 flex-none cursor-pointer rounded border border-gray-200 bg-white p-0.5" />
      <span className="w-20 flex-none">{label}</span>
      <input
        aria-label={`${label} colour hex value`}
        aria-invalid={!valid}
        value={text}
        maxLength={7}
        disabled={disabled}
        onChange={(e) => {
          setText(e.target.value);
          if (HEX.test(e.target.value)) onChange(e.target.value.toLowerCase());
        }}
        className={`w-24 rounded-lg border px-2 py-1 text-xs tabular-nums focus:outline-none ${valid ? "border-gray-200 text-gray-600 focus:border-indigo-500" : "border-red-400 text-red-700"}`}
      />
    </div>
  );
}

/**
 * Live mock-up of the layout and branding, so the merchant sees the template before saving. The store name and contact
 * details are real text styled with CSS (never text baked into an image). The preview is the base template only: no sample product boxes.
 * Sizes use container-query units, so it scales with its width and keeps the chosen aspect ratio.
 */
export function TemplatePreview({ draft }) {
  const [downloading, setDownloading] = useState(false);
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { skipped } = await downloadTemplatePreview(draft);
      if (skipped.length) message.warning(`Downloaded without the ${skipped.join(" and ")}: it couldn't be loaded for the image.`);
    } catch (err) {
      message.error(err.message || "Couldn't download the preview.");
    } finally {
      setDownloading(false);
    }
  };
  const { brand, background, layout } = draft.config;
  const [w, h] = layout.aspectRatio.split(":").map(Number);
  const slots = layout.slots;
  const center = layout.storeNamePosition === "center";
  const contact = brand.contact || {};
  const contactParts = [contact.phone, contact.website, contact.address].filter(Boolean);
  const fontH = brand.fonts.heading || undefined;
  const fontB = brand.fonts.body || undefined;
  // Background: the AI-made image (when there is one), the reference image, or a plain colour.
  const bgImage = background.type === "ai" && draft.backgroundImageUrl ? draft.backgroundImageUrl : background.type === "image" && draft.referenceImageUrl ? draft.referenceImageUrl : null;
  const bg = bgImage ? { backgroundColor: background.color, backgroundImage: `url(${bgImage})`, backgroundSize: "cover", backgroundPosition: "center" } : { background: background.color };

  const contrast = layout.storeNameContrast ?? 40;
  const logoCenter = layout.logoPosition === "center";
  const logoContrast = layout.logoContrast ?? 100;
  const logoScale = (layout.logoSize ?? 100) / 100;
  const logoEl = (position) =>
    draft.logoUrl && (
      <img src={draft.logoUrl} alt="" data-testid="preview-logo" data-position={position} data-size={Math.round(logoScale * 100)} className="w-auto object-contain" style={{ height: `${(position === "center" ? 11 : 6) * logoScale}cqw`, maxWidth: `${Math.min(30 * logoScale, 90)}cqw`, ...(position === "center" ? { opacity: logoContrast / 100 } : {}) }} />
    );
  const hasName = !!brand.businessName.trim();
  const nameEl = (position) =>
    hasName && (
    <div
      className="max-w-full truncate text-center font-bold leading-tight"
      style={{ color: brand.colors.primary, fontFamily: fontH, fontSize: position === "center" ? "clamp(24px, 14cqw, 110px)" : "clamp(13px, 4.6cqw, 30px)", ...(position === "center" ? { opacity: contrast / 100 } : {}) }}
      data-testid="preview-store-name"
      data-position={position}
      {...(position === "center" ? { "data-layer": "behind", "data-contrast": contrast } : {})}
    >
      {brand.businessName}
    </div>
  );
  const topLogo = !logoCenter && logoEl("top");
  const centerLogo = logoCenter && logoEl("center");
  const hasTop = (!center && hasName) || !!topLogo;
  const hasCenter = (center && hasName) || !!centerLogo;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className={labelCls}>Preview</span>
        <button type="button" onClick={handleDownload} disabled={downloading} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:border-indigo-400 hover:text-indigo-700 disabled:opacity-50">
          {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
          Download preview
        </button>
      </div>
      <div className="w-full overflow-hidden rounded-xl border border-gray-200" style={{ aspectRatio: `${w} / ${h}`, containerType: "inline-size", ...bg }} data-testid="template-preview">
        <div className="flex h-full flex-col" style={{ gap: "1.8cqw", padding: "2.4cqw" }}>
          {/* Header: whatever is placed at the top (logo and/or store name), centered. */}
          {hasTop && (
            <div className="flex flex-none items-center justify-center gap-[1.6cqw]" data-testid="preview-header">
              {topLogo}
              {!center && nameEl("top")}
            </div>
          )}
          {/* Open area. Anything placed in the center sits here, in the middle. */}
          <div className="relative min-h-0 flex-1">
            {hasCenter && (
              <div className="pointer-events-none absolute inset-0 z-0 flex flex-col items-center justify-center gap-[1.6cqw]" data-testid="preview-center">
                {centerLogo}
                {center && nameEl("center")}
              </div>
            )}
          </div>
          {slots.promo.enabled && slots.promo.text && (
            <div className="flex-none truncate rounded px-2 py-1 text-center font-semibold text-[clamp(8px,2.2cqw,13px)]" style={{ background: brand.colors.accent, color: brand.colors.primary }}>
              {slots.promo.text}
            </div>
          )}
          {/* Contact details live in their own footer row, so they can never sit on top of the products. */}
          <div className="flex flex-none flex-wrap items-center justify-center gap-x-[2.4cqw] gap-y-0.5 border-t pt-[1.2cqw] text-center text-[clamp(7px,2cqw,12px)]" style={{ borderColor: brand.colors.accent, color: brand.colors.text, fontFamily: fontB, opacity: contactParts.length ? 1 : 0.5 }} data-testid="preview-contact">
            {contactParts.length ? contactParts.map((t, i) => <span key={i} className="max-w-full truncate">{t}</span>) : <span>Phone · Website · Address</span>}
          </div>
        </div>
      </div>
      <span className="text-xs text-gray-500">A quick layout sketch. The final image is created by AI from this brief{draft.referenceImageUrl ? " and your reference image" : ""}.</span>
    </div>
  );
}

export default function BaseTemplateCreation({ onRemove }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [draft, setDraft] = useState(null); // the form; null until something is selected
  const [saved, setSaved] = useState(null); // what the server has for `draft`, to know if it was edited
  const [busy, setBusy] = useState(""); // "save" | "approve" | "delete"
  const [formError, setFormError] = useState("");
  const [startedFromOnboarding, setStartedFromOnboarding] = useState(false);
  const [savedPrompts, setSavedPrompts] = useState([]); // prompts this store used for backgrounds, newest first
  const [savedPromptId, setSavedPromptId] = useState("");
  const [showErrors, setShowErrors] = useState(false); // show "required" messages only after a save attempt

  // `others` = existing templates to copy store details from (none passed for "New template", which starts with empty store details).
  const select = useCallback((t, others = []) => {
    const d = t ? fromTemplate(t) : blankDraft(others.length ? withDefaults(others[0].config) : null);
    setDraft(d);
    setSaved(t ? clone(fieldsOf(d)) : null);
    setFormError("");
    setShowErrors(false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      let list = await listTemplates();
      // First visit for a new store: create its starting template from onboarding details (once; safe to repeat).
      if (list.length === 0) {
        try {
          const first = await ensureInitialTemplate();
          if (first) {
            list = [first];
            setStartedFromOnboarding(true);
          }
        } catch {
          /* no starting template is fine: the merchant can create one by hand */
        }
      }
      setTemplates(list);
      select(list[0] || null, list);
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, [select]);

  useEffect(() => {
    load();
    window.addEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, load);
    return () => window.removeEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, load);
  }, [load]);

  const loadSavedPrompts = useCallback(() => {
    listBackgroundPrompts()
      .then(setSavedPrompts)
      .catch(() => setSavedPrompts([])); // saved prompts are a convenience: the card works without them
  }, []);

  useEffect(() => {
    loadSavedPrompts();
    window.addEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, loadSavedPrompts);
    return () => window.removeEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, loadSavedPrompts);
  }, [loadSavedPrompts]);

  const dirty = useMemo(() => !!draft && JSON.stringify(fieldsOf(draft)) !== JSON.stringify(saved), [draft, saved]);

  const setConfig = (fn) =>
    setDraft((d) => {
      const config = clone(d.config);
      fn(config);
      return { ...d, config };
    });

  const upsertLocal = (t) => setTemplates((prev) => (prev.some((x) => x.id === t.id) ? prev.map((x) => (x.id === t.id ? t : x)) : [...prev, t]).sort((a, b) => a.name.localeCompare(b.name)));

  const handleSave = async () => {
    setFormError("");
    if (!draft.name.trim()) return setFormError("Give the template a name.");
    const problems = storeInfoErrors(draft.config.brand);
    if (Object.keys(problems).length) {
      setShowErrors(true);
      return setFormError(Object.values(problems)[0]);
    }
    setBusy("save");
    try {
      const t = draft.id ? await updateTemplate(draft.id, fieldsOf(draft)) : await createTemplate(fieldsOf(draft));
      upsertLocal(t);
      select(t);
      message.success(draft.id ? "Template saved. Approve it to use it in Image Creation." : "Template created. Review it, then approve it.");
    } catch (err) {
      setFormError(err.message);
    } finally {
      setBusy("");
    }
  };

  const handleCreateBackground = async () => {
    setFormError("");
    const prompt = draft.config.background.prompt.trim();
    if (!prompt) return setFormError("Describe the background you want.");
    setBusy("background");
    try {
      const { key, url, promptColors } = await generateTemplateBackground({ prompt, aspectRatio: draft.config.layout.aspectRatio, colors: draft.config.brand.colors });
      // Colours the customer named in the description also become the template's colours (primary, secondary, accent).
      const named = Array.isArray(promptColors) ? promptColors : [];
      setDraft((d) => {
        const colors = { ...d.config.brand.colors };
        ["primary", "secondary", "accent"].forEach((k, i) => { if (named[i]) colors[k] = named[i]; });
        return { ...d, backgroundImageKey: key, backgroundImageUrl: url, config: { ...d.config, brand: { ...d.config.brand, colors }, background: { ...d.config.background, type: "ai" } } };
      });
      message.success("Background created. Check the preview, then save the template.");
      // Keep the prompt that was used, so it can be picked again later (best effort: never blocks the result).
      saveBackgroundPrompt(prompt)
        .then((saved) => setSavedPrompts((list) => [saved, ...list.filter((p) => p.id !== saved.id)]))
        .catch(() => {});
    } catch (err) {
      setFormError(err.message);
    } finally {
      setBusy("");
    }
  };

  const handleDeleteSavedPrompt = async () => {
    if (!savedPromptId) return;
    try {
      await deletePrompt(savedPromptId);
      setSavedPrompts((list) => list.filter((p) => p.id !== savedPromptId));
      setSavedPromptId("");
    } catch (err) {
      setFormError(err.message);
    }
  };

  const handleApprove = async () => {
    setBusy("approve");
    setFormError("");
    try {
      const t = await approveTemplate(draft.id);
      upsertLocal(t);
      select(t);
      message.success("Template approved. It's now available in Image Creation.");
    } catch (err) {
      setFormError(err.message);
    } finally {
      setBusy("");
    }
  };

  const handleDelete = async () => {
    if (!draft.id || !window.confirm(`Delete the template "${draft.name}"?`)) return;
    setBusy("delete");
    try {
      await deleteTemplate(draft.id);
      const rest = templates.filter((t) => t.id !== draft.id);
      setTemplates(rest);
      select(rest[0] || null, rest);
      message.success("Template deleted.");
    } catch (err) {
      setFormError(err.message);
    } finally {
      setBusy("");
    }
  };

  const disabled = !!busy;
  const cfg = draft?.config;
  const errs = cfg ? storeInfoErrors(cfg.brand) : {};

  return (
    <article className="rounded-2xl border border-gray-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 sm:px-5">
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-indigo-50 text-indigo-600">
            <LayoutTemplate size={18} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 sm:text-base">Base Template Creation</h3>
            <p className="text-xs text-gray-500">Save your branding and layout once, then reuse it for every image</p>
          </div>
        </div>
        {onRemove && (
          <button type="button" onClick={onRemove} disabled={disabled} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-gray-400 disabled:opacity-50">
            Remove from Components
          </button>
        )}
      </header>

      {loading ? (
        <div className="flex items-center gap-2 p-6 text-sm text-gray-500">
          <Loader2 size={16} className="animate-spin" /> Loading your templates…
        </div>
      ) : loadError ? (
        <div className="m-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{loadError}</span>
          <button type="button" onClick={load} className="font-semibold underline">Try again</button>
        </div>
      ) : (
        <div className="flex flex-col gap-4 p-4 sm:p-5">
          {startedFromOnboarding && draft?.source === "onboarding" && (
            <p className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-900">
              We made a starting template from your business details. Add your logo, colours and fonts, check the preview, then approve it.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Templates">
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={draft?.id === t.id}
                disabled={disabled}
                onClick={() => select(t)}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs disabled:opacity-50 ${draft?.id === t.id ? "border-indigo-500 bg-indigo-50 font-semibold text-indigo-700" : "border-gray-200 text-gray-600 hover:border-gray-400"}`}
              >
                {t.name}
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${t.status === "approved" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{t.status === "approved" ? "Approved" : "Draft"}</span>
              </button>
            ))}
            <button type="button" disabled={disabled} onClick={() => select(null)} className="inline-flex items-center gap-1 rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:border-indigo-400 disabled:opacity-50">
              <Plus size={12} /> New template
            </button>
          </div>

          {draft && (
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              {/* ---------- Form ---------- */}
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="bt-name" className={labelCls}>Template name</label>
                  <input id="bt-name" className={inputCls} value={draft.name} maxLength={80} disabled={disabled} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="e.g. Lunch menu board" />
                </div>
                <fieldset className="flex flex-col gap-3 rounded-xl border border-gray-200 p-3" disabled={disabled}>
                  <legend className={`${labelCls} px-1`}>Store information</legend>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="bt-business" className={labelCls}>Store name <span className="font-normal text-gray-400">(optional)</span></label>
                    <input id="bt-business" className={inputCls} value={cfg.brand.businessName} maxLength={120} onChange={(e) => setConfig((c) => { c.brand.businessName = e.target.value; })} placeholder="Shown on the image if you add one" />
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="bt-phone" className={labelCls}>Phone number</label>
                      <input id="bt-phone" type="tel" aria-invalid={!!errs.phone} className={inputCls} value={cfg.brand.contact.phone} maxLength={30} onChange={(e) => setConfig((c) => { c.brand.contact.phone = e.target.value; })} placeholder="e.g. +44 20 7946 0000" />
                      {errs.phone && <span className="text-xs text-red-600">{errs.phone}</span>}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="bt-website" className={labelCls}>Website</label>
                      <input id="bt-website" aria-invalid={!!errs.website} className={inputCls} value={cfg.brand.contact.website} maxLength={200} onChange={(e) => setConfig((c) => { c.brand.contact.website = e.target.value; })} placeholder="e.g. www.example.com" />
                      {errs.website && <span className="text-xs text-red-600">{errs.website}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="bt-address" className={labelCls}>Address</label>
                    <input id="bt-address" className={inputCls} value={cfg.brand.contact.address} maxLength={200} onChange={(e) => setConfig((c) => { c.brand.contact.address = e.target.value; })} placeholder="Street, town, postcode" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className={labelCls}>Store name placement</span>
                    <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Store name placement">
                      {NAME_POSITIONS.map(([v, text]) => (
                        <button key={v} type="button" role="radio" aria-checked={cfg.layout.storeNamePosition === v} onClick={() => setConfig((c) => { c.layout.storeNamePosition = v; })} className={`rounded-lg border px-4 py-1.5 text-sm disabled:opacity-50 ${cfg.layout.storeNamePosition === v ? "border-indigo-500 bg-indigo-50 font-semibold text-indigo-700" : "border-gray-200 text-gray-600 hover:border-gray-400"}`}>
                          {text}
                        </button>
                      ))}
                    </div>
                    <span className="text-xs text-gray-500">Top puts the name in the middle of the header. Center places it in the middle as a background layer behind the products. Contact details always go in the footer.</span>
                    {cfg.layout.storeNamePosition === "center" && (
                      <div className="flex items-center gap-3">
                        <label htmlFor="bt-name-contrast" className="text-xs font-medium text-gray-700">Name contrast</label>
                        <input id="bt-name-contrast" type="range" min={10} max={100} step={5} value={cfg.layout.storeNameContrast} onChange={(e) => setConfig((c) => { c.layout.storeNameContrast = Number(e.target.value); })} className="w-40 accent-indigo-600" />
                        <span className="w-10 text-xs tabular-nums text-gray-500">{cfg.layout.storeNameContrast}%</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className={labelCls}>Background design <span className="font-normal text-gray-400">(optional)</span></span>
                    <div className="flex flex-col gap-2 rounded-xl border border-gray-200 p-3">
                      <label htmlFor="bt-bg-prompt" className={labelCls}>Describe the background</label>
                      <textarea id="bt-bg-prompt" rows={3} className={`${inputCls} resize-y`} value={cfg.background.prompt} maxLength={500} disabled={disabled} onChange={(e) => setConfig((c) => { c.background.prompt = e.target.value; })} placeholder="e.g. soft watercolour leaves along the edges, warm cream paper texture" />
                      <p className="text-xs text-gray-500">Colours you name here are used for the background and for the template colours. If you name none, your brand colours guide it.</p>
                      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Prompt ideas">
                        <span className="text-xs text-gray-500">Ideas:</span>
                        {PROMPT_SUGGESTIONS.map((s) => (
                          <button key={s.label} type="button" disabled={disabled} onClick={() => { setSavedPromptId(""); setConfig((c) => { c.background.prompt = s.prompt; }); }} className="rounded-full border border-gray-200 px-2.5 py-1 text-xs text-gray-700 hover:border-indigo-400 hover:text-indigo-700 disabled:opacity-50">
                            {s.label}
                          </button>
                        ))}
                      </div>
                      {savedPrompts.length > 0 && (
                        <div className="flex items-center gap-2">
                          <select aria-label="Your saved prompts" value={savedPromptId} disabled={disabled} onChange={(e) => { const p = savedPrompts.find((x) => x.id === e.target.value); setSavedPromptId(e.target.value); if (p) setConfig((c) => { c.background.prompt = p.prompt; }); }} className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-900 focus:border-indigo-500 focus:outline-none disabled:bg-gray-50">
                            <option value="">Use a prompt you saved before</option>
                            {savedPrompts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                          {savedPromptId && (
                            <button type="button" onClick={handleDeleteSavedPrompt} disabled={disabled} aria-label="Delete saved prompt" className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:border-red-300 hover:text-red-600 disabled:opacity-50">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <button type="button" onClick={handleCreateBackground} disabled={disabled || !cfg.background.prompt.trim()} className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60">
                          {busy === "background" ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                          {busy === "background" ? "Creating…" : draft.backgroundImageUrl ? "Create again" : "Create background"}
                        </button>
                        {draft.backgroundImageUrl && busy !== "background" && (
                          <button type="button" onClick={() => setDraft((d) => ({ ...d, backgroundImageKey: null, backgroundImageUrl: null, config: { ...d.config, background: { ...d.config.background, type: d.config.background.type === "ai" ? "solid" : d.config.background.type } } }))} disabled={disabled} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-2 text-xs font-medium text-gray-600 hover:border-gray-400 disabled:opacity-50">
                            <X size={12} /> Remove background
                          </button>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {busy === "background"
                          ? "The AI is designing your background. This can take up to a minute."
                          : "Optional. The AI makes artwork only (no text), with calm areas for your name, logo and details. It uses your colours and the chosen aspect ratio, so create it again if you change either. Without one, the plain background colour is used."}
                      </span>
                  </div>
                  </div>
                </fieldset>

                <AssetPicker label="Logo" hint="Optional. PNG, JPG or WebP, up to 8 MB." url={draft.logoUrl} disabled={disabled} kind="logo" onUploaded={({ key, url }) => setDraft((d) => ({ ...d, logoKey: key, logoUrl: url }))} onClear={() => setDraft((d) => ({ ...d, logoKey: null, logoUrl: null }))} />
                <div className="flex flex-col gap-1.5">
                  <span className={labelCls}>Logo placement</span>
                  <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Logo placement">
                    {NAME_POSITIONS.map(([v, text]) => (
                      <button key={v} type="button" role="radio" aria-checked={cfg.layout.logoPosition === v} disabled={disabled} onClick={() => setConfig((c) => { c.layout.logoPosition = v; })} className={`rounded-lg border px-4 py-1.5 text-sm disabled:opacity-50 ${cfg.layout.logoPosition === v ? "border-indigo-500 bg-indigo-50 font-semibold text-indigo-700" : "border-gray-200 text-gray-600 hover:border-gray-400"}`}>
                        {text}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-3">
                    <label htmlFor="bt-logo-size" className="text-xs font-medium text-gray-700">Logo size</label>
                    <input id="bt-logo-size" type="range" min={50} max={400} step={10} value={cfg.layout.logoSize} onChange={(e) => setConfig((c) => { c.layout.logoSize = Number(e.target.value); })} className="w-40 accent-indigo-600" />
                    <span className="w-12 text-xs tabular-nums text-gray-500">{cfg.layout.logoSize}%</span>
                  </div>
                  <span className="text-xs text-gray-500">Top puts the logo in the middle of the header. Center puts it in the middle of the design.</span>
                  {cfg.layout.logoPosition === "center" && (
                    <div className="flex items-center gap-3">
                      <label htmlFor="bt-logo-contrast" className="text-xs font-medium text-gray-700">Logo contrast</label>
                      <input id="bt-logo-contrast" type="range" min={10} max={100} step={5} value={cfg.layout.logoContrast} onChange={(e) => setConfig((c) => { c.layout.logoContrast = Number(e.target.value); })} className="w-40 accent-indigo-600" />
                      <span className="w-10 text-xs tabular-nums text-gray-500">{cfg.layout.logoContrast}%</span>
                    </div>
                  )}
                </div>
                <AssetPicker label="Reference / template image" hint="Optional. An existing design to follow: its layout and style are reused." url={draft.referenceImageUrl} disabled={disabled} kind="reference" onUploaded={({ key, url }) => setDraft((d) => ({ ...d, referenceImageKey: key, referenceImageUrl: url }))} onClear={() => setDraft((d) => ({ ...d, referenceImageKey: null, referenceImageUrl: null }))} />

                <fieldset className="flex flex-col gap-2" disabled={disabled}>
                  <legend className={labelCls}>Brand colours <span className="font-normal text-gray-400">(optional)</span></legend>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {COLOR_FIELDS.map(([key, text]) => (
                      <ColorField key={key} label={text} value={cfg.brand.colors[key]} disabled={disabled} onChange={(v) => setConfig((c) => { c.brand.colors[key] = v; if (key === "background" && c.background.type === "solid") c.background.color = v; })} />
                    ))}
                  </div>
                </fieldset>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="bt-font-h" className={labelCls}>Heading font</label>
                    <FontSelect id="bt-font-h" value={cfg.brand.fonts.heading} disabled={disabled} onChange={(v) => setConfig((c) => { c.brand.fonts.heading = v; })} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="bt-font-b" className={labelCls}>Body font</label>
                    <FontSelect id="bt-font-b" value={cfg.brand.fonts.body} disabled={disabled} onChange={(v) => setConfig((c) => { c.brand.fonts.body = v; })} />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="bt-bg" className={labelCls}>Background</label>
                  <select id="bt-bg" className={inputCls} value={cfg.background.type} disabled={disabled} onChange={(e) => setConfig((c) => { c.background.type = e.target.value; })}>
                    {BACKGROUNDS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                  </select>
                </div>
                {cfg.background.type === "ai" ? (
                  <p className="text-xs text-gray-500">This background comes from the description in Store information above. Edit the description there and create it again to change it.</p>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="bt-bg-note" className={labelCls}>Background notes <span className="font-normal text-gray-400">(optional)</span></label>
                    <input id="bt-bg-note" className={inputCls} value={cfg.background.note} maxLength={300} disabled={disabled} onChange={(e) => setConfig((c) => { c.background.note = e.target.value; })} placeholder="e.g. subtle paper texture, warm lighting" />
                  </div>
                )}

                <div className="flex flex-col gap-1.5">
                  <span className={labelCls}>Aspect ratio</span>
                  <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Aspect ratio">
                    {ASPECT_RATIOS.map((r) => (
                      <button key={r} type="button" role="radio" aria-checked={cfg.layout.aspectRatio === r} disabled={disabled} onClick={() => setConfig((c) => { c.layout.aspectRatio = r; })} className={`rounded-lg border px-2.5 py-1.5 text-xs disabled:opacity-50 ${cfg.layout.aspectRatio === r ? "border-indigo-500 bg-indigo-50 font-semibold text-indigo-700" : "border-gray-200 text-gray-600 hover:border-gray-400"}`}>
                        {r}
                      </button>
                    ))}
                  </div>
                </div>

              </div>

              {/* ---------- Preview + actions ---------- */}
              <div className="flex flex-col gap-4">
                <TemplatePreview draft={draft} />

                {formError && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{formError}</p>}

                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={handleSave} disabled={disabled || (!!draft.id && !dirty)} className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60">
                    {busy === "save" ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    {draft.id ? "Save changes" : "Save template"}
                  </button>
                  {draft.id && draft.status !== "approved" && (
                    <button type="button" onClick={handleApprove} disabled={disabled || dirty} title={dirty ? "Save your changes first" : undefined} className="inline-flex items-center justify-center gap-2 rounded-lg border border-green-600 px-4 py-2.5 text-sm font-semibold text-green-700 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-60">
                      {busy === "approve" ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                      Approve for use
                    </button>
                  )}
                  {draft.id && (
                    <button type="button" onClick={handleDelete} disabled={disabled} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2.5 text-sm font-medium text-gray-600 hover:border-red-300 hover:text-red-600 disabled:opacity-50">
                      {busy === "delete" ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                      Delete
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  {draft.status === "approved" && !dirty
                    ? "Approved — pick this template in Image Creation to apply it. Editing it sends it back to draft until you approve it again."
                    : draft.id
                      ? "Draft — approve it to make it available in Image Creation."
                      : "Save the template first, then approve it."}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}
